import {
  getDataPointForLoincCode,
  getDataPointForMarkerName,
} from "@src/packages/content/biomarkers";
import type { ResponseBody } from "@src/packages/openapi/helpers";
import type { paths } from "@src/packages/openapi/schema";
import { getCodedBiomarkerIdentity } from "./biomarker-identity";
import type {
  CanonicalResultRange,
  CanonicalResultSourceKind,
  CanonicalTestResult,
} from "./canonical-results";

export type ClinicalObservation = ResponseBody<
  paths["/patients/{patientId}/clinical-observations"]["get"]
>["data"]["items"][number];

export type DiagnosticReport = ResponseBody<
  paths["/patients/{patientId}/diagnostic-reports/{reportId}"]["get"]
>["data"];

export type CanonicalResultReport = {
  id: DiagnosticReport["id"];
  title: DiagnosticReport["title"];
  studyType: DiagnosticReport["studyType"];
  status: DiagnosticReport["status"];
  issuedAt: DiagnosticReport["issuedAt"];
  orderId: DiagnosticReport["orderId"];
  correctsReportId: DiagnosticReport["correctsReportId"];
  correctionReason: DiagnosticReport["correctionReason"];
  observationIds: string[];
  evidenceEntityIds: string[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toDisplayString(value: unknown): string {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (typeof value === "boolean") return value ? "true" : "false";

  if (!isRecord(value)) return "";
  if ("value" in value) return toDisplayString(value.value);
  if (typeof value.display === "string") return value.display;

  return "";
}

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string" || value.trim() === "") return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getValueUnit(value: unknown): string | null {
  if (!isRecord(value)) return null;
  if (typeof value.unit === "string" && value.unit !== "") return value.unit;
  if (
    typeof value.originalUnit === "string" &&
    value.originalUnit !== ""
  ) {
    return value.originalUnit;
  }
  if (typeof value.numeratorUnit === "string") return value.numeratorUnit;
  return null;
}

export function getCanonicalObservationValue(
  observation: Pick<
    ClinicalObservation,
    "originalValue" | "normalizedValue" | "value"
  >,
): string {
  if (observation.originalValue !== null && observation.originalValue !== "") {
    return observation.originalValue;
  }
  if (observation.normalizedValue !== null) {
    return String(observation.normalizedValue);
  }
  return toDisplayString(observation.value);
}

function getCanonicalObservationUnit(
  observation: Pick<
    ClinicalObservation,
    "originalUnit" | "normalizedUnit" | "value"
  >,
): string {
  return (
    observation.originalUnit ??
    observation.normalizedUnit ??
    getValueUnit(observation.value) ??
    ""
  );
}

function getObservationRange(
  observation: Pick<
    ClinicalObservation,
    "referenceRanges" | "referenceRange" | "normalizedValue"
  >,
): CanonicalResultRange {
  const reference = observation.referenceRanges[0];
  const referenceRecord = isRecord(reference) ? reference : null;
  const fallbackRecord = isRecord(observation.referenceRange)
    ? observation.referenceRange
    : null;

  /*
   * `low` and `high`, not `min` and `max`.
   *
   * Those two were speculative fallbacks that a loose type allowed. The
   * observation's `referenceRange` is validated `.strict()` server-side as
   * `{ low?, high?, unit?, text? }`, so a `min` or a `max` cannot come back
   * from it — reading them was dead code that only looked like tolerance.
   */
  const min = toNumber(
    referenceRecord?.parsedLow ??
      referenceRecord?.originalLow ??
      fallbackRecord?.low,
  );
  const max = toNumber(
    referenceRecord?.parsedHigh ??
      referenceRecord?.originalHigh ??
      fallbackRecord?.high,
  );
  const baseUnit =
    (typeof referenceRecord?.originalUnit === "string"
      ? referenceRecord.originalUnit
      : null) ??
    (typeof fallbackRecord?.unit === "string" ? fallbackRecord.unit : null);

  return {
    min,
    max,
    baseUnit,
    convertedValue: observation.normalizedValue,
  };
}

function getClinicalAssessment(
  interpretation: ClinicalObservation["interpretation"],
): "low" | "normal" | "high" | "unknown" {
  if (!interpretation) return "unknown";

  switch (interpretation.trim().toLowerCase()) {
    case "low":
    case "l":
    case "below":
    case "below_low_normal":
      return "low";
    case "normal":
    case "n":
    case "within_range":
      return "normal";
    case "high":
    case "h":
    case "above":
    case "above_high_normal":
      return "high";
    default:
      return "unknown";
  }
}

function getObservationLoincCode(observation: ClinicalObservation): string | null {
  if (!observation.code || !observation.codeSystem) return null;
  return observation.codeSystem.toLowerCase().includes("loinc")
    ? observation.code
    : null;
}

function getObservationSourceKind(
  observation: ClinicalObservation,
): CanonicalResultSourceKind {
  if (observation.reportId) return "report-derived";
  if (observation.evidence.length > 0) return "uploaded-document";
  return "standalone";
}

export function toCanonicalTestResultFromObservation(
  observation: ClinicalObservation,
): CanonicalTestResult {
  // Resolve by clinical code before label. A canonical observation's
  // `rawLabel` is the coding catalogue's display string — "Spermatozoa
  // [#/volume] in Semen" — which shares no words with the marker name a
  // patient reads, so every coded semen component resolved to nothing and
  // its panel card read "not recorded" while the value sat in the database.
  const loincCode = getObservationLoincCode(observation);
  const dataPoint =
    getDataPointForLoincCode(loincCode) ??
    getDataPointForMarkerName(observation.rawLabel);
  const value = getCanonicalObservationValue(observation);

  return {
    id: observation.id,
    clinicalEntityId: observation.clinicalEntityId,
    dataPointId: dataPoint?.id ?? null,
    identity: getCodedBiomarkerIdentity({
      loincCode,
      name: observation.rawLabel,
    }),
    name: observation.rawLabel,
    value,
    valueType:
      observation.valueKind === "quantity" ||
      observation.valueKind === "ratio" ||
      observation.valueKind === "range"
        ? "numeric"
        : "string",
    unit: getCanonicalObservationUnit(observation),
    date: observation.effectiveAt,
    createdAt: observation.createdAt,
    type: null,
    category: null,
    phaseId: null,
    cycleId: null,
    patientValue: null,
    doctorValue: null,
    patientAssessmentNormal: null,
    notes: null,
    clinicalAssessment: getClinicalAssessment(observation.interpretation),
    clinicalAssessmentDetails: observation.interpretation,
    loincCode,
    labReferenceRange: getObservationRange(observation),
    source: {
      kind: getObservationSourceKind(observation),
      origin: observation.source,
      ownership: observation.ownership,
      ownerId: observation.ownerId,
      providerId: null,
      sourceId: observation.sourceId,
      verificationStatus: observation.verificationStatus,
      verifiedAt: observation.verifiedAt,
      verifiedBy: observation.verifiedBy,
      verificationNotes: observation.verificationNotes,
      attachment: null,
      reportId: observation.reportId,
      orderId: observation.orderId,
      evidenceEntityIds: observation.evidence.map(
        (evidence) => evidence.evidenceEntityId,
      ),
      observationStatus: observation.status,
      reviewStatus: observation.reviewStatus,
      supersedesObservationId: observation.supersedesObservationId,
      correctionReason: observation.correctionReason,
      legacyTestResultId: observation.legacyTestResultId,
    },
  };
}

export function toCanonicalResultReport(
  report: DiagnosticReport,
): CanonicalResultReport {
  return {
    id: report.id,
    title: report.title,
    studyType: report.studyType,
    status: report.status,
    issuedAt: report.issuedAt,
    orderId: report.orderId,
    correctsReportId: report.correctsReportId,
    correctionReason: report.correctionReason,
    observationIds: report.observations.map((observation) => observation.id),
    evidenceEntityIds: report.observations.flatMap((observation) =>
      observation.evidence.map((evidence) => evidence.evidenceEntityId),
    ),
  };
}
